/*
 * Thin wrapper around liquidGL (https://github.com/naughtyduk/liquidGL, MIT).
 *
 * - loads the library only in the browser (the site is pre-rendered with SSG)
 * - waits for web fonts and images so the refracted snapshot is complete
 * - keeps track of every lens so they can be re-captured after layout changes
 *   (route changes, accordions opening, posts loading)
 * - drives rendering from the GSAP ticker, in step with Lenis smooth scroll
 */
import { useEffect } from 'react';

let libPromise = null;
const live = new Set();

export function loadLiquidGL() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (!libPromise) {
    libPromise = import('liquid-gl').then((m) => m.default || window.liquidGL).catch((err) => {
      console.warn('liquidGL failed to load', err);
      return null;
    });
  }
  return libPromise;
}

function imagesIn(el) {
  if (!el) return Promise.resolve();
  const imgs = Array.from(el.querySelectorAll('img'));
  return Promise.all(
    imgs.map((img) => {
      if (img.complete) return null;
      img.loading = 'eager';
      return new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
        setTimeout(resolve, 4000);
      });
    })
  );
}

let syncedFor = null;
async function syncTicker(liquidGL) {
  try {
    const renderer = window.__liquidGLRenderer__;
    if (!renderer || syncedFor === renderer) return;
    const { gsap } = await import('gsap');
    const { ScrollTrigger } = await import('gsap/ScrollTrigger');
    // lenis: false — Lenis is already advanced by our own GSAP ticker in App.jsx
    liquidGL.syncWith({ gsap, ScrollTrigger, lenis: false });
    syncedFor = renderer;
  } catch {
    /* rendering falls back to liquidGL's own requestAnimationFrame loop */
  }
}

/**
 * Create one or more glass lenses. Resolves to a handle with destroy().
 * options are passed straight to liquidGL(); `snapshot` defaults to body.
 */
export async function createGlass(options) {
  const liquidGL = await loadLiquidGL();
  if (!liquidGL || !document.querySelector(options.target)) return { lenses: [], destroy() {} };
  if (document.fonts && document.fonts.ready) await document.fonts.ready;
  const snap = options.snapshot || 'body';
  // a body snapshot is refreshed on window load anyway, so don't hold the lens back for it
  if (snap !== 'body') await imagesIn(document.querySelector(snap));
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let result;
  try {
    result = liquidGL({
      reveal: 'fade',
      ...options,
      ...(reduce ? { specular: false, interaction: 'none', tilt: false } : null),
    });
  } catch (err) {
    console.warn('liquidGL init failed', err);
    return { lenses: [], destroy() {} };
  }
  const lenses = !result ? [] : Array.isArray(result) ? result : [result];
  lenses.forEach((l) => live.add(l));
  syncTicker(liquidGL);
  return {
    lenses,
    destroy() {
      lenses.forEach((l) => {
        live.delete(l);
        try {
          l.destroy?.();
        } catch {
          /* already gone */
        }
      });
    },
  };
}

let recaptureTimer = 0;
/** Re-snapshot the page behind every lens (debounced). */
export function recaptureGlass(delay = 250) {
  if (typeof window === 'undefined') return;
  clearTimeout(recaptureTimer);
  recaptureTimer = setTimeout(() => {
    const done = new Set();
    for (const lens of live) {
      const r = lens.renderer;
      if (r && !done.has(r) && typeof r.captureSnapshot === 'function') {
        done.add(r);
        try {
          r.captureSnapshot();
        } catch {
          /* ignore */
        }
      }
    }
  }, delay);
}

/**
 * React hook: make `target` a liquid glass pane while the component is mounted.
 * media: optional media query; the lens is only created when it matches.
 * delay: ms to wait before the snapshot (lets entrance animations finish).
 */
export function useLiquidGlass(target, options = {}, { media, delay = 0, deps = [] } = {}) {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let handle = null;
    let cancelled = false;
    let timer = 0;
    const mq = media ? window.matchMedia(media) : null;

    const start = () => {
      if (handle || cancelled) return;
      timer = window.setTimeout(async () => {
        const h = await createGlass({ target, ...options });
        if (cancelled) h.destroy();
        else handle = h;
      }, delay);
    };
    const stop = () => {
      clearTimeout(timer);
      handle?.destroy();
      handle = null;
    };
    const onChange = () => (mq.matches ? start() : stop());

    if (!mq || mq.matches) start();
    mq?.addEventListener('change', onChange);
    return () => {
      cancelled = true;
      mq?.removeEventListener('change', onChange);
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, media, delay, ...deps]);
}
